import { useState } from "react";
import {
  ClientResponse,
  IPagination,
  PaginationStateProps,
} from "../config/types";
const INITIAL_PAGE_SIZE = 30;

export const usePagination = <T extends any>(
  ftn: (params: any) => Promise<ClientResponse<T>>,
  pageSize: number = INITIAL_PAGE_SIZE
) => {
  const [pagination, setPagination] = useState<PaginationStateProps>({
    current_page: 1,
    page_size: pageSize,
    total_pages: null,
    hasPreviousPage: false,
    hasNextPage: false,
  });

  const paginateToNextPage =
    <K extends typeof ftn>(
      queryParams?: Parameters<K>[0],
      callbackUpdateState?: (value: T) => void
    ) =>
    async (): Promise<ClientResponse<T>> => {
      if (!pagination.hasNextPage || pagination.current_page == null) {
        return {
          success: false,
          message: "No more pages",
        };
      }
      setPagination((p) => ({ ...p, processing: { next: true } }));
      const response: ClientResponse<T> = await ftn({
        ...queryParams,
        page: pagination.current_page + 1,
      });
      if (response.pagination) {
        setPagination((prev) => ({
          ...response.pagination!,
          processing: { ...prev.processing, next: false },
        }));
      } else {
        setPagination((p) => ({
          ...p,
          processing: { ...p.processing, next: false },
        }));
      }
      if (response?.data && callbackUpdateState) {
        callbackUpdateState(response.data);
      }
      return response;
    };

  const paginateToPreviousPage =
    <K extends typeof ftn>(
      queryParams?: Parameters<K>[0],
      callbackUpdateState?: (value: T) => void
    ) =>
    async (): Promise<ClientResponse<T>> => {
      if (!pagination.hasPreviousPage || pagination.current_page == null) {
        return {
          success: false,
          message: "No more pages",
        };
      }
      setPagination((p) => ({ ...p, processing: { previous: true } }));
      const response: ClientResponse<T> = await ftn({
        ...queryParams,
        page: pagination.current_page - 1,
      });
      if (response.pagination) {
        setPagination((prev) => ({
          ...response.pagination!,
          processing: { ...prev.processing, previous: false },
        }));
      } else {
        setPagination((p) => ({
          ...p,
          processing: { ...p.processing, previous: false },
        }));
      }
      if (response?.data && callbackUpdateState) {
        callbackUpdateState(response.data);
      }
      return response;
    };

  const paginateToPageNumber =
    <K extends typeof ftn>(
      callbackUpdateState: (value: T) => void,
      queryParams?: Parameters<K>[0]
    ) =>
    async (page: number): Promise<ClientResponse<T>> => {
      if (
        page < 1 ||
        (pagination.total_pages !== null && page > pagination.total_pages)
      ) {
        return {
          success: false,
          message: "Invalid page number",
        };
      }

      setPagination((p) => ({ ...p, processing: { pageNumber: page } }));
      const response: ClientResponse<T> = await ftn({
        ...queryParams,
        page,
        page_size: pagination.page_size,
      });

      setPagination((p) => ({ ...p, processing: { pageNumber: undefined } }));
      if (response.pagination) {
        setPagination({
          ...response.pagination,
          processing: undefined,
        });
      }
      if (response?.data && callbackUpdateState) {
        callbackUpdateState(response.data);
      }
      return response;
    };

  const updatePaginationInitState = (state?: IPagination) => {
    if (state) {
      setPagination((prev) => ({
        ...state,
        processing: prev.processing,
      }));
    }
  };

  return {
    pagination,
    paginateToNextPage,
    paginateToPreviousPage,
    updatePaginationInitState,
    paginateToPageNumber,
  };
};
